import { useCallback, useEffect, useMemo, useState } from "react";
import { Select } from "./ui/Select";
import { Checkbox } from "./ui/controls";
import { LoadingState, EmptyState } from "./ui/states";

type Props = {
  setErr: (e: string | null) => void;
};

type KindFilter = "all" | "text" | "embedding";

type DailyPoint = {
  date: string;
  costUsd: number;
  textCostUsd: number;
  embeddingCostUsd: number;
  groundingCostUsd: number;
  groundingQueries: number;
};

type BreakdownRow = {
  key: string;
  label: string;
  requestCount: number;
  inputTokens: number;
  outputTokens: number;
  groundingQueries?: number;
  costUsd: number;
  costPln: number;
  sharePercent: number;
};

type UsageResponse = {
  pricingMeta: {
    effectiveDate: string;
    sourceUrl: string;
    tier: string;
    priceMode: string;
    usdToPln: number;
  };
  summary: {
    todayUsd: number;
    todayPln: number;
    yesterdayUsd: number;
    yesterdayPln: number;
    avgDailyUsd: number;
    avgDailyPln: number;
    periodTotalUsd: number;
    periodTotalPln: number;
    geminiTotalUsd?: number;
    groundingTotalUsd?: number;
    historicCseTotalUsd?: number;
  };
  dailySeries: DailyPoint[];
  byModel: BreakdownRow[];
  byAction: BreakdownRow[];
  topLeads?: BreakdownRow[];
  byUserCategory?: unknown[];
  systemUnattributedUsd?: number;
  systemUnattributedPln?: number;
  warnings?: string[];
};

async function adminFetch<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: "include" });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `HTTP ${res.status}`);
  return data as T;
}

function normalizeUsage(raw: UsageResponse): UsageResponse {
  return {
    ...raw,
    dailySeries: (raw.dailySeries ?? []).map((d) => ({
      date: d.date,
      costUsd: d.costUsd ?? 0,
      textCostUsd: d.textCostUsd ?? 0,
      embeddingCostUsd: d.embeddingCostUsd ?? 0,
      groundingCostUsd: d.groundingCostUsd ?? 0,
      groundingQueries: d.groundingQueries ?? 0,
    })),
    byModel: raw.byModel ?? [],
    byAction: raw.byAction ?? [],
    topLeads: raw.topLeads ?? [],
    byUserCategory: raw.byUserCategory ?? [],
    warnings: raw.warnings ?? [],
    summary: {
      ...raw.summary,
      geminiTotalUsd: raw.summary?.geminiTotalUsd ?? raw.summary?.periodTotalUsd ?? 0,
      groundingTotalUsd: raw.summary?.groundingTotalUsd ?? 0,
      historicCseTotalUsd: raw.summary?.historicCseTotalUsd ?? 0,
    },
    systemUnattributedUsd: raw.systemUnattributedUsd ?? 0,
    systemUnattributedPln: raw.systemUnattributedPln ?? 0,
  };
}

function fmtMoney(usd: number, pln: number): string {
  return `$${usd.toFixed(2)} (${pln.toFixed(2)} PLN)`;
}

// Wartość pieniężna kafelka KPI: dolary jako główna liczba, przeliczenie na PLN
// pod spodem - mniejsze i wyciszone (--text-muted), mniej wyeksponowane (KP-2).
function KpiMoney({ label, usd, pln }: { label: string; usd: number; pln: number }) {
  return (
    <div className="usage-kpi">
      <span className="usage-kpi__label">{label}</span>
      <span className="usage-kpi__value">${usd.toFixed(2)}</span>
      <span className="usage-kpi__sub">{pln.toFixed(2)} PLN</span>
    </div>
  );
}

function StackedBarChart({ series }: { series: DailyPoint[] }) {
  const max = useMemo(
    () => Math.max(...series.map((d) => d.costUsd), 0.0001),
    [series],
  );

  if (series.length === 0) {
    return <EmptyState title="Brak danych za ten okres" desc="Zmień zakres dat lub filtry, aby zobaczyć koszty." />;
  }

  return (
    <div className="usage-chart">
      <div className="usage-chart__legend">
        <span className="usage-chart__legend-item usage-chart__legend-item--text">LLM tekst</span>
        <span className="usage-chart__legend-item usage-chart__legend-item--embedding">Embeddingi</span>
      </div>
      <div className="usage-chart__bars" role="img" aria-label="Wykres kosztów dziennych">
        {series.map((d) => {
          const totalPct = (d.costUsd / max) * 100;
          return (
            <div
              key={d.date}
              className="usage-chart__col"
              title={`${d.date}: $${d.costUsd.toFixed(2)}`}
            >
              <div className="usage-chart__stack" style={{ height: `${Math.max(totalPct, d.costUsd > 0 ? 2 : 0)}%` }}>
                {d.embeddingCostUsd > 0 ? (
                  <div
                    className="usage-chart__seg usage-chart__seg--embedding"
                    style={{ flexGrow: d.embeddingCostUsd }}
                  />
                ) : null}
                {d.textCostUsd > 0 ? (
                  <div className="usage-chart__seg usage-chart__seg--text" style={{ flexGrow: d.textCostUsd }} />
                ) : null}
              </div>
              <span className="usage-chart__label">{d.date.slice(5)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BreakdownTable({ title, rows }: { title: string; rows: BreakdownRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nazwa</th>
              <th>Requesty</th>
              <th>Tokeny wej.</th>
              <th>Tokeny wyj.</th>
              <th>Koszt</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td>{r.requestCount}</td>
                <td>{r.inputTokens}</td>
                <td>{r.outputTokens}</td>
                <td>{fmtMoney(r.costUsd, r.costPln)}</td>
                <td>{r.sharePercent.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function LlmUsageDashboard({ setErr }: Props) {
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState<KindFilter>("all");
  const [includeEmbeddings, setIncludeEmbeddings] = useState(true);
  const [model, setModel] = useState("");
  const [action, setAction] = useState("");
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setErr(null);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("days", String(days));
      params.set("kind", kind);
      if (includeEmbeddings) params.set("includeEmbeddings", "true");
      if (model.trim()) params.set("model", model.trim());
      if (action.trim()) params.set("action", action.trim());
      const r = await adminFetch<UsageResponse>(`/admin/api/llm-usage?${params.toString()}`);
      setData(normalizeUsage(r));
    } catch (e) {
      setErr(String((e as Error).message));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, kind, includeEmbeddings, model, action, setErr]);

  useEffect(() => {
    void load();
  }, [load]);

  const modelOptions = useMemo(() => data?.byModel.map((m) => m.key) ?? [], [data]);

  if (loading && !data) {
    return (
      <div className="panel">
        <LoadingState text="Ładowanie danych..." />
      </div>
    );
  }

  const summary = data?.summary;
  const geminiTotal = summary?.geminiTotalUsd ?? summary?.periodTotalUsd ?? 0;

  return (
    <div className="view-stack llm-usage-dashboard">
      <div className="panel">
      <h3 style={{ marginTop: 0 }}>Zużycie i koszty</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        Szacowany koszt wg cennika Google Gemini (tokeny). To nie jest faktura - wartości mogą różnić się od
        rozliczenia GCP.
      </p>
      {data?.pricingMeta ? (
        <p className="muted" style={{ fontSize: "0.9rem" }}>
          Cennik Gemini obowiązuje od: <strong>{data.pricingMeta.effectiveDate}</strong> · kurs:{" "}
          <strong>1 USD = {data.pricingMeta.usdToPln.toFixed(2)} PLN</strong> ·{" "}
          <a href={data.pricingMeta.sourceUrl} target="_blank" rel="noreferrer">
            Źródło cen Google
          </a>
        </p>
      ) : null}

      <div className="usage-filters">
        <label>
          Okres
          <Select
            ariaLabel="Okres"
            value={String(days)}
            options={[
              { value: "7", label: "7 dni" },
              { value: "30", label: "30 dni" },
              { value: "90", label: "90 dni" },
            ]}
            onChange={(v) => setDays(Number(v))}
          />
        </label>
        <label>
          Rodzaj
          <Select
            ariaLabel="Rodzaj"
            value={kind}
            options={[
              { value: "all", label: "Wszystko" },
              { value: "text", label: "Tylko LLM tekst" },
              { value: "embedding", label: "Tylko embeddingi" },
            ]}
            onChange={(v) => setKind(v as KindFilter)}
          />
        </label>
        <label>
          Model
          <Select
            ariaLabel="Model"
            value={model}
            options={[{ value: "", label: "(wszystkie)" }, ...modelOptions.map((m) => ({ value: m, label: m }))]}
            onChange={setModel}
          />
        </label>
        <label>
          Akcja
          <input type="text" value={action} onChange={(e) => setAction(e.target.value)} placeholder="np. admin_chat" />
        </label>
        <Checkbox checked={includeEmbeddings} onChange={setIncludeEmbeddings}>
          Embeddingi
        </Checkbox>
        <button type="button" className="btn-secondary" disabled={loading} onClick={() => void load()}>
          {loading ? "Ładowanie…" : "Odśwież"}
        </button>
      </div>

      {(data?.warnings?.length ?? 0) > 0 ? (
        <ul className="usage-warnings">
          {data!.warnings!.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}

      {data && summary ? (
        <div className="usage-kpi-grid">
          <KpiMoney label="Dziś" usd={summary.todayUsd} pln={summary.todayPln} />
          <KpiMoney label="Wczoraj" usd={summary.yesterdayUsd} pln={summary.yesterdayPln} />
          <KpiMoney label="Średnio / dzień" usd={summary.avgDailyUsd} pln={summary.avgDailyPln} />
          <KpiMoney label="Suma okresu" usd={summary.periodTotalUsd} pln={summary.periodTotalPln} />
          <div className="usage-kpi">
            <span className="usage-kpi__label">Google Gemini (tokeny)</span>
            <span className="usage-kpi__value">${geminiTotal.toFixed(2)}</span>
          </div>
        </div>
      ) : null}
      </div>

      {data && summary ? (
        <>
          <div className="panel">
            <h3 style={{ marginTop: 0 }}>Koszt dzienny</h3>
            <StackedBarChart series={data.dailySeries} />
          </div>
          <BreakdownTable title="Per model" rows={data.byModel} />
          <BreakdownTable title="Per akcja" rows={data.byAction} />
        </>
      ) : null}
    </div>
  );
}
